/**
 * MET — Metriek (Meten & Meetkunde) rulebook.
 *
 * Sourced from DEFAULT_POLICY (Metriek entries) +
 * docs/audits/DIDACTISCH_AUDIT_MET_2026.md + MODULE_AUTHORING_GUIDE §3.
 */

import type { ModuleRulebook } from './types';
export const MET_RULEBOOK: ModuleRulebook = {
  moduleId: 'Metriek',
  shortCode: 'MET',
  displayName: 'Metriek',
  didactics: {
    principles: ['eenheid_benoemen', 'formule_expliciet', 'metrieke_trap', 'visueel_meten'],
    schoolbookReferences: ['Wereld in Getallen', 'Pluspunt'],
    misconceptionDomain: 'met',
    coreSummary: 'Meten en meetkunde: eenheden, omrekenen via de metrieke trap, formules voor omtrek/opp/inhoud, en meetkundige eigenschappen.'
  },
  notation: {
    requireCommaDecimals: true,
    requireSuperscriptUnits: true,
    forbiddenPatterns: [{
      pattern: '\\bcm2\\b|\\bm2\\b|\\bkm2\\b',
      label: 'Eenheden zonder superscript (gebruik cm², m², km²)'
    }, {
      pattern: '\\bcm3\\b|\\bm3\\b',
      label: 'Eenheden zonder superscript (gebruik cm³, m³)'
    }]
  },
  content: {
    distractorMisconceptionPattern: 'formule|oppervlakte|omtrek|inhoud|eenheid|omrekenen'
  },
  explanation: {
    requireRuleReference: true,
    checkStrategyMinTier: null,
    preferredStrategyKeywords: ['metrieke trap', 'omrekenen', 'formule', 'eenheid']
  },
  visuals: {
    preferredAidTypes: ['geometricShape', 'compositeShape', 'circle', 'stairs', 'ruler', 'protractor', 'measuringCup', 'weighScale', 'box3d', 'geo', 'symmetryGrid', 'ratioTable'],
    familyVisualHints: [
    // — Hoeken (specific before generic ^MET-HOEK-) —
    {
      familyIdPattern: '^MET-HOEK-METEN$',
      preferredStimulus: 'protractor',
      note: 'Hoeken meten: gradenboog op de hoek.'
    }, {
      familyIdPattern: '^MET-HOEK-TEKENEN$',
      preferredStimulus: 'protractor',
      note: 'Hoek tekenen met de gradenboog.'
    }, {
      familyIdPattern: '^MET-HOEK-BEREKENEN$',
      preferredSupport: 'rule',
      roleScope: 'rule-only',
      note: 'Hoek-berekening (driehoek/vierhoek): de gegeven hoeken worden via een tekst-rule aangeleverd (hoek A = 65 graden, hoek B = 45 graden, hoek C = ?). Pre-juni 2026 hint was geometricShape, maar de GeometricShape-component rendert een opp/afmeting-figuur (shape+base+height) en kan GEEN hoekwaarden annoteren; een geometricShape-stimulus zou een misleidende figuur met irrelevante afmetingen tonen. Tot er een hoek-geannoteerde meetkundige visual bestaat is rule-only didactisch correct. Heroverweeg zodra een dergelijke visual beschikbaar is.'
    }, {
      familyIdPattern: '^MET-HOEK-SOORTEN',
      preferredStimulus: 'protractor',
      note: 'Soorten hoeken: gradenboog toont één hoek met showValue:false zodat de leerling het soort moet herkennen. (Pre-juni 2026 hint geometricShape was incorrect — een hele driehoek toont meerdere hoeken tegelijk, daarmee verdwijnt de "welk soort is DEZE hoek"-prikkel. 6/6 seeds gebruikten al protractor.)'
    }, {
      familyIdPattern: '^MET-HOEK-',
      preferredStimulus: 'protractor',
      note: 'Default: hoeken via gradenboog.'
    },
    // — Lengte —
    {
      familyIdPattern: '^MET-LEN-(METEN|NAUWKEURIG)$',
      preferredStimulus: 'ruler',
      note: 'Lengte meten met liniaal.'
    }, {
      familyIdPattern: '^MET-LEN-SCHATTEN$',
      preferredStimulus: 'ruler',
      note: 'Schatten met referentie-liniaal.'
    }, {
      familyIdPattern: '^MET-LEN-OMREKEN',
      preferredSupport: 'stairs',
      roleScope: 'rule-only',
      note: 'Lengte-omrekenen via metrieke trap.'
    },
    // — Gewicht —
    {
      familyIdPattern: '^MET-GEW-WEGEN$',
      preferredStimulus: 'weighScale',
      note: 'Gewicht aflezen op weegschaal.'
    }, {
      familyIdPattern: '^MET-GEW-OMREKEN',
      preferredSupport: 'stairs',
      roleScope: 'rule-only',
      note: 'Gewicht-omrekenen via metrieke trap.'
    },
    // — Inhoud —
    // INH-LITER/CITER zijn tier-9 brug-families: het gaat primair om
    // omrekenen (l ↔ ml ↔ dl ↔ m³), niet om aflezen. De metrieke trap
    // is dus het didactisch dragende beeld; maatbeker mag als variant
    // (één á twee vragen) maar is geen preferred stimulus.
    {
      familyIdPattern: '^MET-INH-(LITER|CITER)$',
      preferredStimulus: 'stairs',
      note: 'Inhoud-brug via metrieke trap (l ↔ ml ↔ dl ↔ m³). Maatbeker mag als variant.'
    }, {
      familyIdPattern: '^MET-INH-BLOK$',
      preferredStimulus: 'box3d',
      autoExemptEmbedded: true,
      note: 'Inhoud van een blok in 3D tonen. autoExemptEmbedded dekt step_solver / fill_in waar de renderer de berekening voert.'
    }, {
      familyIdPattern: '^MET-INH-OMREKEN',
      preferredSupport: 'stairs',
      roleScope: 'rule-only',
      note: 'Inhoud-omrekenen via metrieke trap (lineair en kubiek).'
    }, {
      familyIdPattern: '^MET-INH-SCHATTEN$',
      preferredStimulus: 'measuringCup',
      note: 'Inhoud schatten met referentie (maatbeker / vertrouwde houder).'
    },
    // — Meetkunde (cirkel-specifiek vóór generic ^MET-MEETK-) —
    {
      familyIdPattern: '^MET-MEETK-(CIRKEL-DEEL|DIAMETER|OMTREK)$',
      preferredStimulus: 'circle',
      note: 'Cirkel met gemarkeerde diameter/straal/omtrek.'
    }, {
      familyIdPattern: '^MET-MEETK-OPP$',
      preferredStimulus: 'geometricShape',
      stimulusAllowed: ['circle'],
      note: 'Vorm met lengte × breedte gemarkeerd. circle is ook geldig wanneer de vraag een cirkel-oppervlakte betreft (πr²) — sub-skill meetk_opp_cirkel.'
    }, {
      familyIdPattern: '^MET-DRIEHOEK-SOORTEN',
      preferredStimulus: 'geometricShape',
      note: 'Driehoek-typen visueel onderscheiden.'
    }, {
      familyIdPattern: '^MET-SYMMETRIE',
      preferredStimulus: 'symmetryGrid',
      note: 'Symmetrie-as op een raster.'
    }, {
      familyIdPattern: '^MET-COORDINATEN',
      preferredStimulus: 'coordinateGrid',
      note: 'Coördinatenstelsel met assen — de coordinateGrid-component is het specifieke visual (pre-juni 2026 hint "geo" was te generiek; 6/6 seeds gebruikten al coordinateGrid).'
    },
    // — Schaal (no hard support hint; seeds use stairs/ratioTable/numberline interchangeably) —
    {
      familyIdPattern: '^MET-SCHAAL-OPP$',
      preferredSupport: 'stairs',
      note: 'Schaal-oppervlakte: kwadrateren via trap of verhoudingstabel.'
    }, {
      familyIdPattern: '^MET-SCHAAL-',
      preferredSupport: 'ratioTable',
      note: 'Schaal-verband: verhoudingstabel is voorkeur, stairs/numberline acceptabel als didactisch passend.'
    },
    // — Oppervlakte: specifics vóór generic ^MET-OPP- —
    // MET-OPP-OMREKENEN en MET-OPP-LAND zijn omrekeningsfamilies, geen
    // vorm-met-afmetingen families — de metrieke trap is dragend.
    {
      familyIdPattern: '^MET-OPP-OMREKENEN$',
      preferredSupport: 'stairs',
      roleScope: 'rule-only',
      note: 'Oppervlakte-omrekenen: kwadratische metrieke trap (×100/÷100 per trede).'
    }, {
      familyIdPattern: '^MET-OPP-LAND$',
      preferredSupport: 'stairs',
      roleScope: 'rule-only',
      note: 'Land-oppervlakte (ha/km²/m²): omrekenen via trap, geen vorm-stimulus nodig.'
    }, {
      familyIdPattern: '^MET-OPP-',
      preferredStimulus: 'geometricShape',
      autoExemptEmbedded: true,
      note: 'Oppervlakte-families tonen de vorm met afmetingen. autoExemptEmbedded dekt grid_shade / step_solver met eigen visueel oppervlak.'
    }, {
      familyIdPattern: '^MET-OMTREK-',
      preferredStimulus: 'geometricShape',
      autoExemptEmbedded: true,
      note: 'Omtrek-families tonen de vorm met alle zijden gemarkeerd. autoExemptEmbedded dekt step_solver / fill_in met eigen visueel oppervlak.'
    }]
  },
  questionTypes: {
    preferredTypes: ['multiple_choice', 'fill_in', 'estimation_slider', 'thermometer_slider', 'grid_shade', 'categorize', 'step_solver', 'error_spotting', 'ratio_table', 'comparison_judge', 'ordering'],
    discouragedTypes: [{
      type: 'true_false_series',
      reason: 'P0 audit fix: true_false is recognition-only en past niet bij metriek.'
    }],
    // Didactische rol-definities per visueel type. Toegevoegd in
    // RULEBOOKS_DIDACTIC_AUDIT_2026 (P0-fix): voor MET stonden 11
    // preferredTypes zonder enkele rol-uitleg, waardoor de governance
    // pipeline niet kon onderscheiden tussen een didactische keuze en
    // een willekeurige type-selectie. Elke entry koppelt DOK, tier-range
    // en de specifieke distractor-val die het type uitlokt.
    customTypeRoles: [{
      type: 'grid_shade',
      didacticRole: 'visual',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 10
      },
      reason: 'Raster-shading bij oppervlakte/omtrek: het kind telt en kleurt vakjes — bouwt het verschil tussen "zijden tellen" (omtrek) en "vakjes tellen" (oppervlakte) op via directe manipulatie. Lokt de met.omtrek-aantal-zijden-vierkant en met.opp-omtrek-verward val uit.'
    }, {
      type: 'categorize',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 12
      },
      reason: 'Sorteren van eenheden (mm/cm/m/km) of hoeksoorten (scherp/recht/stomp): de prefix-verwarring (met.hoek-type-verward, met.inhoud-eenheid-verward) wordt zichtbaar in WELKE bin het kind kiest, niet in het cijfer-antwoord.'
    }, {
      type: 'ratio_table',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 6,
        max: 14
      },
      reason: 'Verhoudingstabel bij schaal- en eenheidsomrekening: maakt factor-10 of factor-100 stappen expliciet kolomsgewijs. Voorkomt dat het kind in één stap raadt; lokt de met.eenheid-factor-10 en met.opp-stap-factor-10 vallen uit.'
    }, {
      type: 'estimation_slider',
      didacticRole: 'concept',
      preferredDok: 1,
      appropriateTiers: {
        min: 2,
        max: 8
      },
      reason: 'Schatten van lengte/inhoud/gewicht via slider: bouwt maatgevoel. Geen exact antwoord vereist — een tolerantie-band controleert of het schattings-anker correct gekozen is. Niet voor exacte berekeningen.'
    }, {
      type: 'thermometer_slider',
      didacticRole: 'visual',
      preferredDok: 1,
      appropriateTiers: {
        min: 2,
        max: 8
      },
      reason: 'Temperatuur aflezen of plaatsen op verticale schaal: het kind ziet richting (boven/onder 0) als ruimtelijke metafoor. Lokt richting-omkering-fouten uit zonder vraag-tekst.'
    }, {
      type: 'step_solver',
      didacticRole: 'strategic',
      preferredDok: 2,
      appropriateTiers: {
        min: 6,
        max: 14
      },
      reason: 'Meerstaps oppervlakte/omtrek/inhoud-berekening waarbij de strategie expliciet stap-voor-stap wordt getoond. Bij MET essentieel om "formule kiezen → invullen → eenheid" als drie aparte controle-punten te tonen — voorkomt dat fout-in-stap-1 het hele antwoord wegvaagt.'
    }, {
      type: 'comparison_judge',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 12
      },
      reason: 'Vergelijking van twee meet-resultaten (groter / kleiner / gelijk): het kind oordeelt over correctheid van een uitspraak. Geschikt voor eenheid-context ("2,5 m > 250 cm?") waar de eenheid-trap zichtbaar wordt.'
    }, {
      type: 'error_spotting',
      didacticRole: 'strategic',
      preferredDok: 3,
      appropriateTiers: {
        min: 8,
        max: 14
      },
      reason: 'Fout-spotting in een uitgewerkte meet-berekening: het kind moet de exacte stap aanwijzen waar de eenheid of formule fout gaat. Hogere DOK dan zelf-berekenen omdat het meta-cognitie vereist (waarom is dit fout?).'
    }, {
      type: 'ordering',
      didacticRole: 'concept',
      preferredDok: 2,
      appropriateTiers: {
        min: 4,
        max: 10
      },
      reason: 'Ordenen van eenheden (mm < cm < dm < m < km) of hoeken op grootte: maakt relatieve verhoudingen expliciet. Geen rekenwerk; uitsluitend conceptueel.'
    }],
    familyTypeHints: [{
      familyIdPattern: '^MET-(OPP|OMTREK|MEETK)-',
      preferredTypes: ['fill_in', 'step_solver', 'grid_shade', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Oppervlakte/omtrek/meetkunde: berekening of constructie vereist — geen herkenning.'
    }, {
      familyIdPattern: '^MET-(GEW|LEN|INH)-OMREKEN',
      preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Omrekenen: metrieke trap doorlopen met expliciete berekening.'
    }, {
      familyIdPattern: '^MET-(GEW|LEN|INH)-SCHATTEN',
      preferredTypes: ['estimation_slider', 'multiple_choice', 'matching'],
      note: 'Schatten: slider geeft fijne feedback over schattings-precisie.'
    }, {
      familyIdPattern: '^MET-HOEK-(SOORTEN|BEREKENEN|METEN)',
      preferredTypes: ['categorize', 'fill_in', 'step_solver', 'multiple_choice'],
      discouragedTypes: ['true_false_series'],
      note: 'Hoeken: classificatie (scherp/recht/stomp) of berekening — beide vereisen toepassing.'
    }, {
      familyIdPattern: '^MET-TIJD-(METEN|NAUWKEURIG)$',
      preferredStimulus: 'stopwatch',
      note: 'Tijd meten/nauwkeurig: stopwatch visual voor aflezen, tussentijden en vergelijken.'
    }, {
      familyIdPattern: '^MET-TIJD-',
      preferredTypes: ['fill_in', 'step_solver', 'multiple_choice'],
      note: 'Tijd meten/nauwkeurig: aflezen of berekenen via duur-stappen.'
    }]
  },
  distractorTraps: [{
    id: 'unit-swap',
    label: 'Eenheid verwisselen',
    description: 'cm i.p.v. m of cm² i.p.v. m² kiezen — kerntrap voor metriek.'
  }, {
    id: 'area-not-squared',
    label: 'Oppervlakte zonder kwadrateren',
    description: 'Bij schaal × 10 oppervlakte als × 10 berekenen i.p.v. × 100.'
  }, {
    id: 'formula-confusion',
    label: 'Formule verwisselen',
    description: 'Omtrek-formule bij oppervlakte-vraag of andersom toepassen.'
  }],
  keyRules: ['Eenheden altijd vermelden: cm², m², km²', 'Formules expliciet noemen in uitleg (O = l × b)', 'Geo-visuals verplicht voor oppervlakte/omtrek families', 'Metrieke trap als support-visual bij omrekenen', 'Trap-distractors: eenheid verwisselen, oppervlakte zonder kwadrateren'],
  // GSR-25 (PR-9): per-module override copy migrated from inline
  // `moduleMessages` maps in didacticDepth.ts.
  qualityMessages: {
    explanationDepthRequirement: 'MET explanation must reference formula or conversion (×10, L×B, π × r²)',
    distractorMisconceptionRequirement: 'MET distractors should reference formula or unit error'
  },
  authoringGuideAnchor: '#3-met--meten--meetkunde'
};